/**
 * Admin Config React Query Hooks
 *
 * Provides data fetching and mutation hooks for the generic config system.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { useMemo, useCallback } from 'react';
import { adminConfigApi } from '../api/adminConfigApi';
import { normalizeSalesStages } from './salesStages';
import type {
  AdminConfig,
  ConfigKey,
  ConfigKeyMap,
  UpdateAdminConfigInput,
  SalesStageConfig,
  SolutionCategoryConfig,
  SolutionCategoriesValue,
  SalesYearValue,
  RepTeamsValue,
  HelpPanelValue,
  IntakeMappingsValue,
  IntakeFieldMapping,
  TaskStatusColorsValue,
  HealthRule,
  TaskStatusColorConfig,
} from './types';
import { CONFIG_DEFAULTS } from './defaults';
import { withNewDefaultCategories } from './launcherCategories';

// =============================================================================
// Query Keys
// =============================================================================

export const configKeys = {
  all: ['adminConfig'] as const,
  lists: () => [...configKeys.all, 'list'] as const,
  list: (category?: string) => [...configKeys.lists(), { category }] as const,
  byKey: (key: string) => [...configKeys.all, 'key', key] as const,
};

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Fetch all config entries (for admin listing page)
 */
export function useAdminConfigs(
  category?: string,
  options?: Omit<UseQueryOptions<AdminConfig[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: configKeys.list(category),
    queryFn: () => adminConfigApi.list(category),
    staleTime: 10 * 60 * 1000, // 10 min
    ...options,
  });
}

/**
 * Fetch a single config by its key name — the primary consumer hook.
 * Returns fully typed value based on ConfigKeyMap.
 */
export function useConfigByKey<K extends ConfigKey>(
  key: K,
  options?: Omit<UseQueryOptions<AdminConfig<ConfigKeyMap[K]> | null, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: configKeys.byKey(key),
    queryFn: () => adminConfigApi.getByKey<ConfigKeyMap[K]>(key),
    staleTime: 10 * 60 * 1000,
    ...options,
  });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

/**
 * Update a config value
 */
export function useUpdateConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input, rowVersion }: {
      id: string;
      input: UpdateAdminConfigInput;
      rowVersion?: string;
    }) => adminConfigApi.update(id, input, rowVersion),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: configKeys.all });
    },
  });
}

/**
 * Delete a config row. Used by the duplicate-detector cleanup action.
 */
export function useDeleteConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => adminConfigApi.delete(id),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: configKeys.all });
    },
  });
}

/**
 * Initialize a config row from defaults. Used when an admin clicks
 * "Initialize" on a known config key whose Dataverse row doesn't exist yet.
 */
export function useCreateConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      key: string;
      category: string;
      displayName: string;
      description?: string;
      value: unknown;
    }) => adminConfigApi.create(input),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: configKeys.all });
    },
  });
}

// =============================================================================
// Derived Hooks for Sales Stages
// =============================================================================

// Fallback values come from the central registry in defaults.ts.
const STAGES_FALLBACK: SalesStageConfig[] = CONFIG_DEFAULTS['sales.stages'].value.stages;

export function useStagesConfig(): SalesStageConfig[] {
  const { data } = useConfigByKey('sales.stages');
  const stored = data?.value?.stages;
  // Stored rows predate bpfStageId and the 9/29 Sales Hub cutover; the
  // normalizer fills ids, applies the in-place renames (old names kept as
  // aliases) and adds the Stop Loss Review bucket. See salesStages.ts.
  return useMemo(
    () => (stored ? normalizeSalesStages(stored) : STAGES_FALLBACK),
    [stored],
  );
}

/**
 * Returns a function that remaps a stepName through configured aliases.
 * Stages with `mappedTo` are treated as their target stage for reporting.
 * e.g., if "Finalization" has mappedTo: "Presentation", then
 *   mapStage("Finalization") returns "Presentation"
 */
export function useStageMapping(): (stepName: string | undefined) => string | undefined {
  const stages = useStagesConfig();
  const mapping = useMemo(
    () => {
      const map = new Map<string, string>();
      for (const s of stages) {
        if (s.mappedTo) map.set(s.name, s.mappedTo);
      }
      return map;
    },
    [stages],
  );
  return useCallback(
    (stepName: string | undefined) => {
      if (!stepName) return stepName;
      return mapping.get(stepName) ?? stepName;
    },
    [mapping],
  );
}

/**
 * Returns sorted stage names where isCase is true.
 * Excludes mapped (aliased) stages — their targets already cover them.
 */
export function useCaseStages(): string[] {
  const stages = useStagesConfig();
  return useMemo(
    () => stages
      .filter((s) => s.isCase && !s.mappedTo)
      .sort((a, b) => a.order - b.order)
      .map((s) => s.name),
    [stages],
  );
}

/**
 * Returns case stage names PLUS any alias source names that map into them.
 * Used for filtering — we need to match records at old stage names too.
 */
export function useCaseStagesForMatch(): string[] {
  const stages = useStagesConfig();
  return useMemo(
    () => {
      const caseNames = new Set(
        stages.filter((s) => s.isCase && !s.mappedTo).map((s) => s.name),
      );
      for (const s of stages) {
        if (s.mappedTo && caseNames.has(s.mappedTo)) {
          caseNames.add(s.name);
        }
      }
      return [...caseNames];
    },
    [stages],
  );
}

/**
 * Returns sorted stage names where isPipeline is true.
 * Excludes mapped (aliased) stages.
 */
export function usePipelineStages(): string[] {
  const stages = useStagesConfig();
  return useMemo(
    () => stages
      .filter((s) => s.isPipeline && !s.mappedTo)
      .sort((a, b) => a.order - b.order)
      .map((s) => s.name),
    [stages],
  );
}

/**
 * Returns pipeline stage names PLUS any alias source names that map into them.
 * Used for API queries — we need to fetch records at old stage names too.
 */
export function usePipelineStagesForQuery(): string[] {
  const stages = useStagesConfig();
  return useMemo(
    () => {
      const pipelineNames = new Set(
        stages.filter((s) => s.isPipeline && !s.mappedTo).map((s) => s.name),
      );
      // Add source names that map to a pipeline stage
      for (const s of stages) {
        if (s.mappedTo && pipelineNames.has(s.mappedTo)) {
          pipelineNames.add(s.name);
        }
      }
      return [...pipelineNames];
    },
    [stages],
  );
}

/**
 * Returns BPF stage GUIDs for stages where isCase is true.
 * Used to query dts_veracitybpfstagechangelog for case-stage entries.
 * Excludes mapped aliases — they share their target's BPF stage.
 */
export function useCaseStageBpfIds(): string[] {
  const stages = useStagesConfig();
  return useMemo(
    () => stages
      .filter((s) => s.isCase && !s.mappedTo && s.bpfStageId)
      .map((s) => s.bpfStageId!),
    [stages],
  );
}

/**
 * Returns BPF stage GUIDs for every configured stage, aliases included: the
 * funding-type stop loss stages are aliases of the Stop Loss Review bucket
 * but carry their own ids, and their log rows are real transitions.
 */
export function useAllStageBpfIds(): string[] {
  const stages = useStagesConfig();
  return useMemo(
    () => [...new Set(stages.filter((s) => s.bpfStageId).map((s) => s.bpfStageId!))],
    [stages],
  );
}

/**
 * Returns all active stage names in order (excludes mapped aliases).
 */
export function useAllStages(): string[] {
  const stages = useStagesConfig();
  return useMemo(
    () => stages
      .filter((s) => !s.mappedTo)
      .sort((a, b) => a.order - b.order)
      .map((s) => s.name),
    [stages],
  );
}

/**
 * Returns a map of stage name → hex color.
 * Excludes mapped aliases.
 */
export function useStageColors(): Record<string, string> {
  const stages = useStagesConfig();
  return useMemo(
    () => Object.fromEntries(
      stages
        .filter((s) => s.color && !s.mappedTo)
        .map((s) => [s.name, s.color!]),
    ),
    [stages],
  );
}

// =============================================================================
// Derived Hooks for Implementation Task Status Colors (C7)
// =============================================================================

const TASK_STATUS_COLORS_FALLBACK: TaskStatusColorsValue = CONFIG_DEFAULTS['implementation.taskStatusColors'].value;

/** Transition health thresholds (ADO #1725), config over defaults. */
export function useHealthRules(): HealthRule[] {
  const { data } = useConfigByKey('implementation.healthRules');
  return useMemo(
    () => (data?.value ?? CONFIG_DEFAULTS['implementation.healthRules'].value).rules,
    [data],
  );
}

/** The full task-status definitions (value/name/color/category), config over defaults. */
export function useTaskStatusConfig(): TaskStatusColorConfig[] {
  const { data } = useConfigByKey('implementation.taskStatusColors');
  return useMemo(() => (data?.value ?? TASK_STATUS_COLORS_FALLBACK).statuses, [data]);
}

/** Map of status option-set value → hex color (config over defaults). */
export function useTaskStatusColorMap(): Record<number, string> {
  const statuses = useTaskStatusConfig();
  return useMemo(() => Object.fromEntries(statuses.map((s) => [s.value, s.color])), [statuses]);
}

// =============================================================================
// Derived Hooks for Solution Categories
// =============================================================================

const SOLUTION_CATEGORIES_FALLBACK: SolutionCategoriesValue = CONFIG_DEFAULTS['sales.solutionCategories'].value;

function useSolutionCategoriesConfig(): SolutionCategoriesValue {
  const { data } = useConfigByKey('sales.solutionCategories');
  return data?.value ?? SOLUTION_CATEGORIES_FALLBACK;
}

/**
 * Returns solution categories sorted by order.
 */
export function useSolutionCategories(): SolutionCategoryConfig[] {
  const config = useSolutionCategoriesConfig();
  return useMemo(
    () => [...config.categories].sort((a, b) => a.order - b.order),
    [config],
  );
}

/**
 * Returns a map from Dataverse field name → solution category name.
 * Used to classify an opportunity's products into reporting categories.
 */
export function useSolutionCategoryMap(): {
  fieldToCategory: Record<string, string>;
  fieldLabels: Record<string, string>;
} {
  const config = useSolutionCategoriesConfig();
  return useMemo(() => {
    const fieldToCategory: Record<string, string> = {};
    for (const cat of config.categories) {
      for (const field of cat.fields) {
        fieldToCategory[field] = cat.name;
      }
    }
    return {
      fieldToCategory,
      fieldLabels: config.fieldLabels,
    };
  }, [config]);
}

/**
 * Returns a map from product label (as it appears in opportunity.productsToQuote)
 * to the color of its solution category. Used to render colored product pills.
 *
 * If a product's category has no color, the entry is omitted — callers should
 * render a neutral pill in that case.
 */
export function useProductColors(): Map<string, string> {
  const config = useSolutionCategoriesConfig();
  return useMemo(() => {
    const result = new Map<string, string>();
    for (const cat of config.categories) {
      if (!cat.color) continue;
      for (const field of cat.fields) {
        const label = config.fieldLabels[field] ?? field;
        result.set(label, cat.color);
      }
    }
    return result;
  }, [config]);
}

/**
 * Solution color resolver — accepts a Catalog Solution name (or any human
 * label) and returns the matching admin-config category color, or undefined
 * when no mapping exists. Names are normalized (lowercased, alphanumerics only)
 * so "VeracityRx" matches "Veracity Rx" matches "veracity-rx".
 *
 * Used wherever solution chips/pills are rendered: Overview, top bar,
 * SolutionsPills, Quick Facts → Solutions by Year, All Groups + My Groups
 * tables. Pair with `hexToRgba(color, 0.12)` from @shared/lib for the chip
 * background tint pattern.
 */
export function useSolutionColorLookup(): (name: string) => string | undefined {
  const productColorMap = useProductColors();
  const categories = useSolutionCategories();
  return useMemo(() => {
    const merged = new Map<string, string>();
    const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
    for (const [label, color] of productColorMap) merged.set(norm(label), color);
    for (const cat of categories) {
      if (cat.color) merged.set(norm(cat.name), cat.color);
    }
    return (name: string) => merged.get(norm(name));
  }, [productColorMap, categories]);
}

// =============================================================================
// Derived Hooks for Sales Year
// =============================================================================

// Re-export pure functions from standalone module (no React/Dataverse deps)
export { getSalesYtdRange, getSalesYearRange, isInSalesYear, getSalesYearForDate } from './sales-year';
import { getSalesYtdRange as _getSalesYtdRange, getSalesYearRange as _getSalesYearRange } from './sales-year';

const SALES_YEAR_FALLBACK: SalesYearValue = CONFIG_DEFAULTS['sales.salesYear'].value;

/**
 * Returns the sales year config (start month, 1-based).
 */
export function useSalesYearConfig(): SalesYearValue {
  const { data } = useConfigByKey('sales.salesYear');
  return data?.value ?? SALES_YEAR_FALLBACK;
}

/**
 * Returns the sales year start/end dates for the sales year that contains
 * the given reference date (defaults to today).
 */
export function useSalesYtdRange(
  throughMonth: number,
  throughYear: number,
): { start: Date; end: Date; salesYearLabel: string } {
  const { startMonth } = useSalesYearConfig();
  return useMemo(() => {
    return _getSalesYtdRange(startMonth, throughYear, throughMonth);
  }, [startMonth, throughMonth, throughYear]);
}

/**
 * Returns the full sales year range (start to end) for the sales year
 * containing the given month/year.
 */
export function useSalesYearRange(
  throughMonth: number,
  throughYear: number,
): { start: Date; end: Date; salesYearLabel: string } {
  const { startMonth } = useSalesYearConfig();
  return useMemo(() => {
    return _getSalesYearRange(startMonth, throughYear, throughMonth);
  }, [startMonth, throughMonth, throughYear]);
}

// =============================================================================
// Derived Hooks for Rep Teams
// =============================================================================

const REP_TEAMS_FALLBACK: RepTeamsValue = CONFIG_DEFAULTS['sales.repTeams'].value;

function useRepTeamsConfig(): RepTeamsValue {
  const { data } = useConfigByKey('sales.repTeams');
  return data?.value ?? REP_TEAMS_FALLBACK;
}

/**
 * Returns a mapping function that resolves an ownerId to its team name
 * (if the owner belongs to a configured team) or falls back to the provided ownerName.
 *
 * Also provides `teamIdFor(ownerId)` which returns a stable team identifier
 * (the first memberId) so all team members collapse to the same key.
 */
export function useRepTeamMapping(): {
  /** Returns the team display name if ownerId is in a team, otherwise the original ownerName */
  mapOwnerName: (ownerId: string | undefined, ownerName: string | undefined) => string | undefined;
  /** Returns a stable team key (first memberId) if ownerId is in a team, otherwise the ownerId itself */
  teamIdFor: (ownerId: string | undefined) => string | undefined;
  /**
   * Every owner id behind a dropdown key: the team's member ids when `key` is
   * a team key (its first member id, compared case-insensitively), else
   * `[key]`. Owner-filtered Dataverse queries need this, or a rep team's
   * dashboard counts only its anchor member.
   */
  memberIdsFor: (key: string) => string[];
  /** Lower-cased team key for a user, or the lower-cased user id when not in a team */
  teamKeyOf: (userId: string) => string;
  /** The raw teams config */
  teams: RepTeamsValue['teams'];
} {
  const config = useRepTeamsConfig();

  const { idToTeamName, idToTeamKey, keyToMemberIds } = useMemo(() => {
    const nameMap = new Map<string, string>();
    const keyMap = new Map<string, string>();
    const membersMap = new Map<string, string[]>();
    for (const team of config.teams) {
      if (team.memberIds.length === 0) continue;
      const teamKey = team.memberIds[0]!; // stable key = first member
      membersMap.set(teamKey.toLowerCase(), team.memberIds);
      for (const memberId of team.memberIds) {
        const lower = memberId.toLowerCase();
        nameMap.set(lower, team.name);
        keyMap.set(lower, teamKey.toLowerCase());
      }
    }
    return { idToTeamName: nameMap, idToTeamKey: keyMap, keyToMemberIds: membersMap };
  }, [config]);

  const mapOwnerName = useCallback(
    (ownerId: string | undefined, ownerName: string | undefined) => {
      if (!ownerId) return ownerName;
      return idToTeamName.get(ownerId.toLowerCase()) ?? ownerName;
    },
    [idToTeamName],
  );

  const teamIdFor = useCallback(
    (ownerId: string | undefined) => {
      if (!ownerId) return ownerId;
      return idToTeamKey.get(ownerId.toLowerCase()) ?? ownerId;
    },
    [idToTeamKey],
  );

  const memberIdsFor = useCallback(
    (key: string) => keyToMemberIds.get(key.toLowerCase()) ?? [key],
    [keyToMemberIds],
  );

  const teamKeyOf = useCallback(
    (userId: string) => idToTeamKey.get(userId.toLowerCase()) ?? userId.toLowerCase(),
    [idToTeamKey],
  );

  return { mapOwnerName, teamIdFor, memberIdsFor, teamKeyOf, teams: config.teams };
}

// =============================================================================
// Derived Hook for Help Panel
// =============================================================================

const HELP_PANEL_FALLBACK: HelpPanelValue = CONFIG_DEFAULTS['shell.helpPanel'].value;

/**
 * Returns the Help & Support panel config, with a default fallback. The
 * default mirrors the original hardcoded modal content, so the panel renders
 * sensibly even before an admin saves anything.
 */
export function useHelpPanel(): HelpPanelValue {
  const { data } = useConfigByKey('shell.helpPanel');
  return data?.value ?? HELP_PANEL_FALLBACK;
}

// =============================================================================
// Derived Hook for Launcher Categories
// =============================================================================

const LAUNCHER_CATEGORIES_FALLBACK = CONFIG_DEFAULTS['shell.launcherCategories'].value;

/**
 * Returns the launcher-category organization (section order, labels, colors,
 * and module assignments) with the code-level default as fallback. The
 * fallback also covers users whose Dataverse access cannot read
 * vba_adminconfig (open-to-all module users without a team): the query
 * errors, data stays undefined, and the launcher renders the defaults.
 */
export function useLauncherCategories() {
  const { data } = useConfigByKey('shell.launcherCategories');
  const saved = data?.value;
  return useMemo(
    () => (saved ? withNewDefaultCategories(saved, LAUNCHER_CATEGORIES_FALLBACK) : LAUNCHER_CATEGORIES_FALLBACK),
    [saved]
  );
}

/**
 * Admin-chosen accent for one module, or `undefined` to use its code default.
 *
 * ONE resolver so the three surfaces that render a module's color (the
 * launcher tile, the header switcher, and the AppShell header itself) cannot
 * disagree. Callers keep their own built-in fallback rather than receiving one
 * here, because AppShell's default lives in VERA_APPS while the launcher's
 * lives on MODULE_CATALOG, and those two lists are not going to be merged in
 * this change.
 *
 * Returns undefined rather than a color when unset, which is what lets an
 * admin clear an override back to the locked default by removing the entry.
 */
export function useModuleAccent(moduleId: string | undefined): string | undefined {
  const cfg = useLauncherCategories();
  if (!moduleId) return undefined;
  const hex = cfg.moduleColors?.[moduleId];
  // Guard the stored value: this is admin-entered and reaches an inline style
  // and a CSS custom property. Anything that is not a plain hex is ignored in
  // favour of the code default rather than injected into the DOM.
  return hex && /^#[0-9a-fA-F]{6}$/.test(hex) ? hex : undefined;
}

// =============================================================================
// Derived Hook for Intake Field Mappings
// =============================================================================

const INTAKE_MAPPINGS_FALLBACK: IntakeMappingsValue = CONFIG_DEFAULTS['intake.fieldMappings'].value;

/**
 * Returns the configured intake field mappings, with an empty fallback when
 * the Dataverse row doesn't exist. Consumers in Ops resolve and apply these
 * after the Catalog seed step completes.
 */
export function useIntakeMappings(): IntakeFieldMapping[] {
  const { data } = useConfigByKey('intake.fieldMappings');
  const value = data?.value ?? INTAKE_MAPPINGS_FALLBACK;
  return value.mappings;
}
